import { AtomicContext } from '@contexts/coveo/AtomicContext';
import {
  buildUrlManager,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/atomic-react';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef, useState } from 'react';

export function useUrlManager(context: AtomicContext) {
  const { searchEngine } = useContext(context);

  const currentHash = useRef(window.location.hash.slice(1));
  const [urlManagerController] = useState(
    buildUrlManager(searchEngine!, {
      initialState: {
        fragment: window.location.hash.slice(1),
      },
    })
  );
  const [urlManagerState, setUrlManagerState] = useState(
    urlManagerController?.state
  );
  const searchParams = useSearchParams();

  // Watch URL for manual changes (like a Link) and synchronize with Coveo controller
  useEffect(() => {
    const hash = window.location.hash.slice(1);

    // Don't synchronize on first render, only when hash actually changes
    if (currentHash.current !== hash) {
      urlManagerController.synchronize(hash);
      const analyticsAction = loadSearchAnalyticsActions(
        searchEngine!
      ).logSearchFromLink();
      const action = loadSearchActions(searchEngine!).executeSearch(
        analyticsAction
      );
      searchEngine!.dispatch(action);
      currentHash.current = hash;
    }
  }, [searchParams, searchEngine, urlManagerController]);

  useEffect(() => {
    setUrlManagerState({ fragment: window.location.hash.slice(1) });

    const unsubscribeManager = urlManagerController?.subscribe(() => {
      const fragment = urlManagerController.state.fragment;

      // Update browser URL if fragment changed
      const newHash = fragment ? `#${fragment}` : '';
      if (newHash !== window.location.hash) {
        const fullUrl = `${window.location.pathname}${window.location.search}${newHash}`;
        window.history.replaceState(null, '', fullUrl);
      }

      setUrlManagerState({ fragment });
      currentHash.current = fragment;
    });

    return () => {
      unsubscribeManager();
    };
  }, [urlManagerController]);

  return {
    urlManagerState,
  };
}
